import type { Order, OrderMode } from '@/store/types.ts';
import type { Way } from '@/types/common.ts';
import type { ISODate, ISODateTime, StringTime } from '@/types/date.ts';

type TabId = string;
export type BulkStopLossState = {
  orderIds: string[];
  orderById: Record<string, StopLossBulkOrder>;
  orderByBdrId: StopLossByBdrid;
};

export type BulkFixingState = {
  orderIds: string[];
  orderById: Record<string, FixingBulkOrder>;
  orderByBdrId: FixingByBdrid;
};

export type BulkStateData = {
  stopLoss: BulkStopLossState;
  fixing: BulkFixingState;
};

export type FxOrderBulksState = Record<TabId, BulkStateData>;

export type BdrId = string;
export type CurrencyPair = string;

export type StopLossByBdrid = Record<BdrId, StopLossByCurrencyPair>;
export type FixingByBdrid = Record<BdrId, FixingByCurrencyPair>;
export type StopLossByCurrencyPair = Record<CurrencyPair, StopLossBulkOrder[]>;
export type FixingByCurrencyPair = Record<CurrencyPair, FixingBulkOrder[]>;

export type StopLossOrderRow = {
  bdrId: string;
  currencyPair: string;
  way: 'B' | 'A';
  notional: string;
  currency: string;
  limitPrice: string;
  expiryDate?: string;
  trigger: Trigger;
};

export type FixingOrderRow = {
  bdrId: string;
  currencyPair: string;
  way: 'B' | 'A';
  notional: string;
  currency: string;
  fixingReference: string; // fixingBenchmark
  priceType: FixingPriceType; // fixingPriceType
  fixingDate: string; //fixingDateUtc
  deliveryDate: string;
  fixingTime: string;
  fixingPlace: string;
  marginType: FixingMarginType; // fixingMarginType
  commercialMargin: string;
};

export type ImportedOrdersType = {
  StopLoss: StopLossOrderRow[];
  Fixing: FixingOrderRow;
};

export type StopLossBulkOrderProperties = {
  id: string;
  omsId: string | undefined;
  currencyPair: CurrencyPair;
  bdrId: BdrId;
  way: Way;
  notional: number;
  currency: string; // currency one or two
  currencyIndex: 1 | 2;
  limitPrice: number;
  expiry: GoodTillCancelled | ExpiryDate;
  trigger: Trigger;
  notificationEmails: string[];
  status: BulkOrderStatus;
  clockOffset?: number; // offset in milliseconds between the backend and the client
  errors: StopLossOrderError[];

  // to detect if the order is in EDIT or VIEW mode
  // EDIT mode is used when the user is editing an existing order
  // VIEW mode is used when the user is viewing an existing order
  mode: OrderMode;
};

export type StopLossBulkOrder = Order<'StopLossOrder', StopLossBulkOrderProperties>;

export type FixingBulkOrderProperties = {
  id: string;
  omsId: string | undefined;
  bdrId: string;
  currencyPair: string;
  way: 'Bid' | 'Ask';
  notional: number;
  currency: string;
  currencyIndex: 1 | 2;
  fixingReference: string;
  fixingPlace: string;
  fixingTime: string;
  fixingPriceType: FixingPriceType;
  fixingDateUtc: string | undefined;
  deliveryDate: string | undefined;
  commercialMargin: number | undefined;
  fixingMarginType: FixingMarginType;

  notificationEmails: string[];
  status: BulkOrderStatus;
  clockOffset?: number; // offset in milliseconds between the backend and the client
  errors: FixingOrderError[];

  // to detect if the order is in EDIT or VIEW mode
  // EDIT mode is used when the user is editing an existing order
  // VIEW mode is used when the user is viewing an existing order
  mode: OrderMode;
};

export type FixingBulkOrder = Order<'FixingOrder', FixingBulkOrderProperties>;

export enum BulkOrderStatus {
  IDLE = 0,
  VALIDATING = 1,
  VALID = 2,
  INVALID = 3,
  PENDING = 4,
  SUCCESS = 5,
  FAILED = 6,
}

export type StopLossOrderError = {
  field?: StopLossOrderFieldName;
  message: string;
  code: number;
};

export enum StopLossOrderFieldName {
  id = 'id',
  currencyPair = 'currencyPair',
  bdrId = 'bdrId',
  way = 'way',
  notional = 'notional',
  currency = 'currency',
  limitPrice = 'limitPrice',
  expiryDate = 'expiryDate',
  trigger = 'trigger',
  notificationEmails = 'notificationEmails',
}

export type FixingOrderError = {
  field?: FixingOrderFieldName;
  message: string;
  code: number;
};

export enum FixingOrderFieldName {
  id = 'id',
  currencyPair = 'currencyPair',
  bdrId = 'bdrId',
  way = 'way',
  notional = 'notional',
  currency = 'currency',
  fixingReference = 'fixingReference',
  fixingPlace = 'fixingPlace',
  fixingPriceType = 'fixingPriceType',
  fixingDateUtc = 'fixingDateUtc',
  deliveryDate = 'deliveryDate',
  fixingTime = 'fixingTime',
  fixingMarginType = 'fixingMarginType',
  commercialMargin = 'commercialMargin',
  notificationEmails = 'notificationEmails',
}

export const createInitialOrderBulksState = (tabId: string): FxOrderBulksState => ({
  [tabId]: {
    stopLoss: { orderIds: [], orderById: {}, orderByBdrId: {} },
    fixing: { orderIds: [], orderById: {}, orderByBdrId: {} },
  },
});

export const triggers = ['Spot', 'BidOffer', 'RateOneTouch'] as const;
export type Trigger = (typeof triggers)[number];

export const fixingPriceTypes = ['MID', 'BIDASK'] as const;
export type FixingPriceType = (typeof fixingPriceTypes)[number];

export const fixingMarginTypes = ['BidAskMidSpread', 'MarginInBps'] as const;
export type FixingMarginType = (typeof fixingMarginTypes)[number];

export enum Step {
  UserInput = 1,
  TradeNegotiation = 2,
  TradeConfirmation = 3,
}

export const finalStatuses = ['Done', 'Finished', 'Cancelled', 'Expired', 'Partially done', 'InError', 'Rejected'];

export type GoodTillCancelled = {
  type: 'GoodTillCancelled';
};

export type ExpiryDate = {
  type: 'ExpiryDate';
  date?: ISODate;
  time?: StringTime;
  dateTime?: ISODateTime;
};
